import React, { Component } from 'react';
import { Form } from 'redux-form';
import { browserHistory } from 'react-router';
import last from 'lodash/last';
import pick from 'lodash/pick';
import PageTransition from 'react-router-page-transition';
import { getCurrentStepIndex, getFieldPropsFromDefinition, getFeatureProjectFields
  } from 'pap/utils';
import PAPQuestionBase from 'pap/components/common/pap_question_base';
import PAPBase from 'pap/containers/common/pap_base';
import { getPapUrl, showAllQuestionsAtOnce } from 'pap_v2/utils';
import { trackGoogle } from 'common/tracking';
import {
  trackClickWithMixpanel, MP_CHANGED_PAP_STEP_EVENT_NAME, MP_CHANGED_PAP_SECTION_EVENT_NAME
} from 'common/tracking';

/**
 * A component responsible for rendering steps not related to brief questions
 * (features, awards, ICA contract, etc).
 *
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
export default class Details extends Component {
  constructor(props) {
    super(props);

    this.getCurrentStepIndex = this.getCurrentStepIndex.bind(this);
    this.onPrevStep = this.onPrevStep.bind(this);
    this.onNextStep = this.onNextStep.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  getCurrentStepIndex() {
    const { steps, stepName } = this.props;
    return getCurrentStepIndex(steps, stepName);
  }

  componentDidMount() {
    const { fetchProjectIfNeeded, fetchPricing, fetchCountriesIfNeeded, fetchCustomContracts,
      projectId } = this.props;

    /* fetch project and available packages */
    this.props.fetchProjectIfNeeded({ projectId }).then((res) => {
      fetchPricing(res.data.sub_category);
    });

    fetchCountriesIfNeeded();

    fetchCustomContracts(projectId);
  }

  renderStep() {
    const {
      steps, stepName, displayFullScreen, hideFullScreen, fullScreen, packages,
      countries, features, awards, category, subCategory, projectData, user,
      tips
    } = this.props;

    const fieldDefinition = steps.find(s => s.name === stepName);

    if (!fieldDefinition) {
      return null;
    }

    const fieldProps = getFieldPropsFromDefinition(fieldDefinition, {
      user: user.profile_data,
      currentFeatures: features,
      currentAwards: awards,
      currentTips: tips,
      stepNumber: this.getCurrentStepIndex() + 1,
      stepCount: steps.length,
      onNextStep: this.onNextStep,
      showDifferentiators: true,
      showTestimonial: true,
      subCategory,
      displayFullScreen,
      hideFullScreen,
      fullScreen,
      packages,
      countries,
      projectData,
    });

    return <PAPQuestionBase {...fieldProps} data-transition-id={`brief-${fieldDefinition.name}`} />;
  }

  onPrevStep() {
    const { steps, stepName, projectId, category, subCategory, projectData } = this.props;
    const currentStepIndex = this.getCurrentStepIndex();
    let prevStepName;

    /* last step */
    if (currentStepIndex === 0) {
      prevStepName = null;
    } else {
      prevStepName = steps[currentStepIndex - 1].name;
    }

    trackClickWithMixpanel(MP_CHANGED_PAP_STEP_EVENT_NAME, {
      categorySlug: category.slug,
      subCategorySlug: subCategory.slug,
      fromStep: stepName,
      toStep: prevStepName,
      sectionName: "details",
      fromIndex: currentStepIndex,
      toIndex: currentStepIndex === 0 ? null : currentStepIndex - 1,
      stepsInSection: steps.length,
      direction: "backward"
    });

    let url, subStep;
    if (prevStepName) {
      url = getPapUrl({ step: 'details', subStep: prevStepName, projectId });
    } else {
      if (showAllQuestionsAtOnce()) {
        subStep = 'all';
      } else {
        subStep = last(projectData.brief_questions).name;
      }
      url = getPapUrl({ step: 'brief', subStep, projectId });
    }
    browserHistory.push(url);
  }

  onNextStep() {
    const { steps, projectId, submit, stepName, selectedPackage, category, subCategory } = this.props;
    const currentStepIndex = this.getCurrentStepIndex();
    let nextStepName;

    /* last step */
    if (currentStepIndex === steps.length - 1) {
      nextStepName = null;
    } else {
      nextStepName = steps[currentStepIndex + 1].name;
    }

    trackClickWithMixpanel(MP_CHANGED_PAP_STEP_EVENT_NAME, {
      categorySlug: category.slug,
      subCategorySlug: subCategory.slug,
      fromStep: stepName,
      toStep: nextStepName,
      sectionName: "details",
      fromIndex: currentStepIndex,
      toIndex: currentStepIndex === steps.length - 1 ? null : currentStepIndex + 1,
      stepsInSection: steps.length,
      direction: "forward"
    });

    if (!nextStepName) {
      trackClickWithMixpanel(MP_CHANGED_PAP_SECTION_EVENT_NAME, {
        categorySlug: category.slug,
        subCategorySlug: subCategory.slug,
        fromSectionName: "details",
        toSectionName: "review",
        direction: "forward"
      });
    }

    if (stepName === 'package' && selectedPackage) {
      trackGoogle("pap", "selected package", selectedPackage.slug);
    }

    submit();
  }

  handleContractUpdates(data) {
    const { updateProjectRequest, projectId, customContract, createCustomContractRequest,
      deleteCustomContractRequest } = this.props

    const contractData = data['contract'];

    /* upload custom contract if provided (replacing current one if it exists) ... */
    if (contractData.isCustom) {
      /* custom contract hasn't changed */
      if (contractData.custom_contract.id) {
        return Promise.resolve();
      }
      return createCustomContractRequest(projectId, contractData.custom_contract);
    }

    /* or update project with information that will be used to create a contract
       out of CS templates, deleting the custom contract if it exists */
    return updateProjectRequest(
      projectId, pick(contractData, ['client_legal_name', 'client_legal_country'])
    ).then(() => {
      if (customContract) {
        return deleteCustomContractRequest(projectId, customContract.id);
      }
    })
  }

  handleSubmit(data) {
    const {
      projectId, stepName, projectData, steps, hasPackageChanged, setUnhandledError,
      updateProjectRequest, updateAwardsRequest, updateFeaturesRequest, fetchProject,
      fetchCustomContracts, user, reset, logout
    } = this.props;
    const currentStepIdx = this.getCurrentStepIndex();
    const isLastStep = currentStepIdx === steps.length - 1;
    let promise = Promise.resolve();

    if (stepName === 'package' && hasPackageChanged) {
      promise = updateProjectRequest(projectId, { original_package: data['package'] });

    } else if (['award_options', 'project_options', 'promotion_options',
                'protection_options'].includes(stepName)) {

      const projectUpdates = {
        /* project fields that can be changed once features are activated */
        ...getFeatureProjectFields(data, projectData),
      };
      promise = updateProjectRequest(projectId, projectUpdates).then((res) => (
        updateAwardsRequest(projectId, res.data.award_rules, data).then(() => (
          updateFeaturesRequest(projectId, res.data.features, data, res.data.billables,
                                user.profile_data)
        ))
      ));

    } else if (stepName === 'contract') {
      promise = this.handleContractUpdates(data);
    } else if (stepName === 'title') {
      promise = updateProjectRequest(projectId, { title: data['title'] });
    }

    return promise.then(() => {
      /* fetch updated data, making sure dirty values are cleared */
      fetchProject(projectId).then(reset);
      if (stepName === 'contract') {
        fetchCustomContracts(projectId);
      }

      /* move on to next step */
      if (isLastStep) {
        browserHistory.push(getPapUrl({ step: 'review', projectId }));
      } else {
        const nextStepName = steps[currentStepIdx + 1].name;
        browserHistory.push(getPapUrl({ step: 'details', subStep: nextStepName, projectId }));
      }
    }).catch((res) => {
      if (res.status === 403) {
        logout(undefined, true);
        return;
      }

      const error = 'Error occured while saving the project';
      setUnhandledError(
        error, { response: res, step: `PAP details (${stepName})` }, 'Please try again.'
      );
    });
  }

  render() {
    const {
      handleSubmit, valid, submitting, steps, features, awards, tips, projectData
    } = this.props;

    return (
      <PAPBase
        loadingData={!projectData}
        sectionName="details"
        extraClasses={["details"]}
        currentStepIndex={this.getCurrentStepIndex()}
        currentSectionStepCount={steps.length}
        onPrevStep={this.onPrevStep}
        onNextStep={this.onNextStep}
        nextEnabled={!submitting && valid}
        prevEnabled={!submitting}
        nextLabel={submitting? 'Saving...' : undefined}
        currentFeatures={features}
        currentAwards={awards}
        currentTips={tips}
        {...this.props}>
          <Form onSubmit={handleSubmit(this.handleSubmit)}>
            <PageTransition timeout={500}>
              { this.renderStep() }
            </PageTransition>
          </Form>
      </PAPBase>
    );
  }
}



// WEBPACK FOOTER //
// ./pap_v2/components/details.jsx